import { useState } from 'react';
import { calmTokens as t } from '../../../shared/tokens';
import { IconCheck, IconEdit, IconTrash, IconX } from '../../../shared/icons';
import { RULE_ORIGIN_KINDS } from '../../../shared/labels';
import { Chip, PillButton } from './ui';
import { inputStyle, monoStyle, selectStyle } from './styles';

const MATCH_LABELS = { exact: 'égal à', prefix: 'commence par', contains: 'contient' };
const GRID = 'minmax(160px, 1.4fr) 110px minmax(160px, 1.2fr) 70px 56px 72px';

function IconBtn({ icon, title, onClick, color, disabled }) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      onClick={onClick}
      disabled={disabled}
      style={{
        width: 28,
        height: 28,
        borderRadius: 8,
        background: 'transparent',
        border: 'none',
        color: color || t.textMute,
        cursor: disabled ? 'not-allowed' : 'pointer',
        display: 'grid',
        placeItems: 'center',
        padding: 0,
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {icon}
    </button>
  );
}

function RuleEditor({ initial, onSave, onCancel, busy, isNew }) {
  const [form, setForm] = useState({
    match: initial?.match || '',
    matchType: initial?.matchType || 'exact',
    label: initial?.label || '',
    emoji: initial?.emoji || '',
  });
  const up = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const valid = form.match.trim() && form.label.trim();
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: GRID,
        gap: 10,
        padding: '10px 16px',
        alignItems: 'center',
        background: 'rgba(165,180,252,0.04)',
      }}
    >
      <input
        aria-label="Libellé banque"
        value={form.match}
        onChange={(e) => up('match', e.target.value.toUpperCase())}
        placeholder="LIBELLÉ BANQUE"
        style={{ ...inputStyle, ...monoStyle, fontSize: 12 }}
        disabled={busy}
      />
      <select aria-label="Type de correspondance" value={form.matchType} onChange={(e) => up('matchType', e.target.value)} style={selectStyle} disabled={busy}>
        {Object.entries(MATCH_LABELS).map(([k, v]) => (
          <option key={k} value={k}>{v}</option>
        ))}
      </select>
      <div style={{ display: 'flex', gap: 6 }}>
        <input aria-label="Emoji" value={form.emoji} onChange={(e) => up('emoji', e.target.value)} placeholder="🙂" style={{ ...inputStyle, width: 44, textAlign: 'center', padding: 0 }} disabled={busy} />
        <input aria-label="Libellé" value={form.label} onChange={(e) => up('label', e.target.value)} placeholder="LIBELLÉ" style={{ ...inputStyle, flex: 1 }} disabled={busy} />
      </div>
      <div />
      <div />
      <div style={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <IconBtn icon={<IconCheck size={14} />} title={isNew ? 'Créer' : 'Enregistrer'} color={t.credit} onClick={() => valid && onSave(form)} disabled={busy || !valid} />
        <IconBtn icon={<IconX size={14} />} title="Annuler" onClick={onCancel} disabled={busy} />
      </div>
    </div>
  );
}

/** Table éditable des règles de libellé. */
export default function LabelRulesTable({ rules, onCreate, onUpdate, onDelete, busy, adding, onAddingChange }) {
  const [editingId, setEditingId] = useState(null);
  const setAdding = (v) => onAddingChange?.(v);

  return (
    <div>
      <div style={{ border: `1px solid ${t.border}`, borderRadius: 14, overflow: 'hidden', background: t.bg }}>
        <div style={{ overflowX: 'auto' }}>
          <div style={{ minWidth: 720 }}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: GRID,
                gap: 10,
                padding: '8px 16px',
                borderBottom: `1px solid ${t.border}`,
              }}
            >
              {['Libellé banque', 'Condition', 'Devient', 'Origine', 'Utilisée', ''].map((h) => (
                <div key={h} style={{ fontSize: 10.5, color: t.textMute, textTransform: 'uppercase', letterSpacing: 0.8 }}>{h}</div>
              ))}
            </div>
            {adding && (
              <RuleEditor
                isNew
                busy={busy}
                onCancel={() => setAdding(false)}
                onSave={async (form) => {
                  await onCreate(form);
                  setAdding(false);
                }}
              />
            )}
            {rules.map((r) =>
              editingId === r._id ? (
                <RuleEditor
                  key={r._id}
                  initial={r}
                  busy={busy}
                  onCancel={() => setEditingId(null)}
                  onSave={async (form) => {
                    await onUpdate(r._id, form);
                    setEditingId(null);
                  }}
                />
              ) : (
                <div
                  key={r._id}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: GRID,
                    gap: 10,
                    padding: '9px 16px',
                    alignItems: 'center',
                    borderTop: `1px solid ${t.border}`,
                  }}
                >
                  <div style={{ fontSize: 12, ...monoStyle, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={r.match}>
                    {r.match}
                  </div>
                  <div style={{ fontSize: 11, color: t.textDim }}>{MATCH_LABELS[r.matchType] || r.matchType}</div>
                  <div style={{ fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {r.emoji ? `${r.emoji} ` : ''}{r.label}
                  </div>
                  <div>
                    <Chip kind={RULE_ORIGIN_KINDS[r.origin]}>{RULE_ORIGIN_KINDS[r.origin]?.label || r.origin}</Chip>
                  </div>
                  <div style={{ fontSize: 12, color: t.textDim, ...monoStyle }}>{r.hits || 0}×</div>
                  <div style={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                    <IconBtn icon={<IconEdit size={13} />} title="Modifier" onClick={() => setEditingId(r._id)} disabled={busy} />
                    <IconBtn icon={<IconTrash size={13} />} title="Supprimer" color={t.debit} onClick={() => onDelete(r._id)} disabled={busy} />
                  </div>
                </div>
              )
            )}
            {rules.length === 0 && !adding && (
              <div style={{ padding: 20, textAlign: 'center', fontSize: 12, color: t.textMute }}>
                Aucune règle. Elles se créent depuis l’apprentissage, l’import, ou en renommant une opération importée.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
